import "./AnovaHistory.css";

import {
  resetAnova,
  setSelectedAnovaIndex,
} from "../../../../actions/resultMap";
import { useDispatch, useSelector } from "react-redux";
import { useInterval, useUpdate } from "react-use";

import { ANALYTICS_EVENTS } from "../../../../constants";
import { Badge } from "reactstrap";
import { ComponentTitle } from "../../../../components/componentTitle/componentTitle";
import PropTypes from "prop-types";
import { PropertyName } from "../../../../components/PropertyName/PropertyName";
import { formatDistanceStrict } from "date-fns";
import { useTracking } from "../../../../analytics";

const AnovaHistoryItem = ({ anova, isSelected, onClick }) => {
  const [response, rawFactors] = anova.linear_model.split("~");
  const factors = rawFactors.split("+");
  return (
    <div
      className={`anova-history-item clickable d-flex flex-column p-1 gap-1 ${
        isSelected ? "selected" : ""
      }`}
      onClick={onClick}
    >
      <div className="d-flex gap-1 align-items-center">
        <span>
          <i className="fa-solid fa-calendar-days me-1" />
          {anova.date}
        </span>
        <span>
          <i className="fa-solid fa-flask me-1" />
          {anova.experiment}
        </span>
        <div className="flex-grow-1" />
        <span className="font-style-italic">
          {formatDistanceStrict(anova.datetime, new Date(), {
            addSuffix: true,
          }).replace("minutes", "min")}
        </span>
      </div>
      <div className="d-flex ">
        <PropertyName
          className="font-weight-bold"
          colorful
          showIcon
          technicalName={response}
        />
        <span className="ms-1 font-style-italic">~</span>
        {factors.map((f, i) =>
          f.includes("*") ? (
            <i
              key={`${f}${i}`}
              className="ms-1 discrete-icon fa-solid fa-link"
              title={f}
            />
          ) : (
            <PropertyName
              key={`${f}${i}`}
              className="ms-1"
              hideName
              showIcon
              technicalName={f}
            />
          )
        )}
        <div className="flex-grow-1" />
        {anova.anova_error && (
          <Badge color="danger">
            <i className="fa-solid fa-triangle-exclamation me-1" />
            Error
          </Badge>
        )}
      </div>
    </div>
  );
};

AnovaHistoryItem.propTypes = {
  anova: PropTypes.shape({
    linear_model: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    experiment: PropTypes.string.isRequired,
    datetime: PropTypes.string.isRequired,
    anova_error: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

export const AnovaHistory = () => {
  const { previousAnovas, selectedAnovaIndex } = useSelector(
    ({ resultMap }) => resultMap.anova
  );
  const dispatch = useDispatch();

  // Auto refresh this component every 10s to keep the relative datetimes coherent
  const update = useUpdate();
  useInterval(update, 5000);

  const { trackEvent } = useTracking();

  return (
    <div className="powerdash-component">
      <ComponentTitle
        icon="fa-solid fa-bars"
        title="Recent ANOVAs"
        topRightContent={
          previousAnovas.length > 0 && (
            <span
              className="clear-button clickable p-1"
              size="sm"
              onClick={() => dispatch(resetAnova())}
            >
              <i className="fa-solid fa-trash discrete-icon me-1" /> Clear
              history
            </span>
          )
        }
      />
      <div className="anova-history d-flex flex-column gap-1">
        {previousAnovas
          .map((anova, index) => (
            <AnovaHistoryItem
              key={anova.datetime}
              anova={anova}
              isSelected={index === selectedAnovaIndex}
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.LOAD_RECENT_ANOVA);
                dispatch(setSelectedAnovaIndex(index));
              }}
            />
          ))
          // Most recent at the top
          .toReversed()}
      </div>
    </div>
  );
};
